import type { Farm } from './farm';
import type { District, Field, MultiPolygon } from './field';
import type { PlantingType } from './plantingType';

/**
 * 地区DTO（バックエンドAPIレスポンス）
 */
export interface AreaApiDTO {
  id: number;
  name: string;
  subRegionId: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * 農場DTO（バックエンドAPIレスポンス）
 */
export interface FarmApiDTO {
  id: number;
  name: string;
  areaId: number;
  areaName: string;
  fieldCount: number;
}

/**
 * GeoJSON MultiPolygon型（バックエンドAPIレスポンス）
 */
export interface GeoJsonMultiPolygon {
  type: 'MultiPolygon';
  coordinates: number[][][][];
}

/**
 * 圃場DTO（バックエンドAPIレスポンス）
 */
export interface FieldApiDTO {
  id: number;
  name: string | null;
  area: number;
  /** 予測収量（t/ha）。未設定時は null。FARMER ロールには返却されない可能性もある */
  predictedYield: number | null;
  geometry: GeoJsonMultiPolygon;
  centroid: { latitude: number; longitude: number } | null;
  areaId: number;
  areaName: string;
  farmId: number;
  farmName: string;
  plantingType: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Spring Data Page形式のレスポンス型
 */
export interface PageApiDTO<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

/**
 * 地区DTOをDistrict型に変換
 * @param dto - バックエンドAPIから取得した地区DTO
 * @returns フロントエンド用のDistrict型
 */
export function mapAreaDtoToDistrict(dto: AreaApiDTO): District {
  return {
    id: String(dto.id),
    name: dto.name,
    subRegionId: dto.subRegionId,
  };
}

/**
 * 農場DTOをFarm型に変換
 * @param dto - バックエンドAPIから取得した農場DTO
 * @returns フロントエンド用のFarm型
 */
export function mapFarmDtoToFarm(dto: FarmApiDTO): Farm {
  return {
    id: dto.id,
    name: dto.name,
    areaId: dto.areaId,
    areaName: dto.areaName,
    fieldCount: dto.fieldCount,
  };
}

/**
 * 圃場DTOをField型に変換
 * イベントデータはfieldEventServiceから取得
 * @param dto - バックエンドAPIから取得した圃場DTO
 * @returns フロントエンド用のField型
 */
export function mapFieldDtoToField(dto: FieldApiDTO): Field {
  return {
    id: String(dto.id),
    name: dto.name ?? undefined,
    area: dto.area,
    predictedYield: dto.predictedYield ?? null,
    geometry: dto.geometry as MultiPolygon,
    centroid: dto.centroid ?? null,
    district: {
      id: String(dto.areaId),
      name: dto.areaName,
    },
    farmId: dto.farmId ? String(dto.farmId) : undefined,
    farmName: dto.farmName ?? undefined,
    plantingType: dto.plantingType as PlantingType,
  };
}
